import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  MenuItem,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack, AddCircle, Delete, Save, CheckCircle, WarningAmber, ErrorOutline } from '@mui/icons-material';
import { PageHeader, headerCellSx } from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { calculateLineGst, determineGstType, GstBreakup } from '../../utils/gstPreview';
import { api } from '../../services/apiClient';
import { productService, Product } from '../../services/productService';
import { customerService, Customer, validateGSTIN, getStateFromGSTIN } from '../../services/customerService';
import { invoiceService, CompanySettings, CreditLimitExceededDetails } from '../../services/invoiceService';
import CreditStatusCard from '../../components/sales/CreditStatusCard';

// ---------------------------------------------------------------------------
// Sales Invoice creation as a dedicated enterprise workspace, replacing the
// old CustomerInvoiceNewPage.tsx (Customer + 5 inputs + item table) and the
// orphaned QuickCreateInvoice.tsx dialog (which submitted fake
// `productId: index + 1` placeholders). This page:
//   - fixes the previously-broken Sales-Order hand-off (SalesOrderDetailPage
//     already linked here with ?salesOrderId=, but nothing read it)
//   - adds an equivalent ?deliveryId= hand-off from DeliveryDetailPage
//   - pulls GSTIN/HSN/GST rate from Customer/Product masters instead of
//     asking the user to retype them
//   - shows a real, non-fabricated CGST/SGST/IGST preview mirroring the
//     backend's exact utils/gst.js arithmetic (see utils/gstPreview.ts)
//   - shows real credit exposure via CreditStatusCard (GET
//     /sales/customers/:id/credit-status) and a structured, overridable
//     CREDIT_LIMIT_EXCEEDED exception banner (Sales.assertCreditLimit)
//   - uses the real POST /sales/orders/:id/invoice and POST
//     /deliveries/:id/invoice endpoints while the pre-filled lines are an
//     unedited copy of the source document; falls back to a manual,
//     item-by-item create the moment anything is edited
//   - flags the one thing this backend genuinely does not support yet
//     (additional-charge ledger posting) rather than pretending to handle it
// ---------------------------------------------------------------------------

const GST_RATE_OPTIONS = [0, 5, 12, 18, 28];

interface SalesOrderItemRef {
  id: number;
  productId?: number | string;
  product_id?: number | string;
  quantity: number | string;
  unitPrice?: number | string;
  unit_price?: number | string;
}

interface SalesOrderRef {
  id: number;
  orderNumber: string;
  customerId?: number | string;
  status: string;
  items: SalesOrderItemRef[];
  related?: {
    enquiry: { id: number; [k: string]: any } | null;
    contract: { id: number; [k: string]: any } | null;
  };
}

interface DeliveryItemRef {
  id?: number;
  product_id?: string | number | null;
  delivered_qty?: number | string | null;
  quantity?: number | string | null;
}

interface DeliveryRef {
  id: number;
  delivery_number: string;
  sales_order_id: number | string;
  sales_order_number?: string | null;
  customer_id?: number | string;
  transporter?: string | null;
  vehicle_no?: string | null;
  transport_mode?: string | null;
  dispatch_from?: string | null;
  deliver_to?: string | null;
  distance_km?: number | string | null;
  warehouse_name?: string | null;
  status: string;
  items: DeliveryItemRef[];
}

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

interface ItemDraft {
  key: string;
  product: Product | null;
  hsnCode: string;
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  gstRate: string;
  priceOverridden: boolean;
}

const emptyItem = (): ItemDraft => ({
  key: nextDraftKey(),
  product: null,
  hsnCode: '',
  quantity: '1',
  unitPrice: '0',
  discountPercent: '0',
  gstRate: '18',
  priceOverridden: false,
});

interface ChargeDraft {
  key: string;
  description: string;
  amount: string;
  taxable: boolean;
  gstRate: string;
}

const emptyCharge = (): ChargeDraft => ({ key: nextDraftKey(), description: '', amount: '0', taxable: true, gstRate: '18' });

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n: number): number => Math.round(n * 100) / 100;
const todayISO = () => new Date().toISOString().slice(0, 10);

// Signature used to detect whether SO-pre-filled lines are still an exact,
// unedited copy of the order — see useOrderBasedCreate below.
const itemSig = (productId: string | number, quantity: number, unitPrice: number, discountPercent: number) =>
  `${productId}:${quantity}:${unitPrice}:${discountPercent}`;

interface CheckResult {
  label: string;
  ok: true | false | 'warn';
  detail?: string;
}

// ---- Real server tax + accounting preview: POST /sales/invoices/preview ----
// (Sales.previewInvoice) — the genuine CGST/SGST/IGST split and the exact GL
// lines (real chart-of-accounts code + name) that would post. gstPreview.ts
// above mirrors the same arithmetic for instant feedback while this request
// is in flight; once it resolves, per-line tax cells and the Accounting
// Preview panel switch to these authoritative, server-computed figures.
interface PreviewLine {
  productId: number | string;
  productName?: string | null;
  hsnCode?: string | null;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  gstRate: number;
  taxableAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  lineTotal: number;
}

interface AccountingPreviewLine {
  code: string;
  name: string;
  debit: number;
  credit: number;
}

interface InvoicePreview {
  taxType: 'INTRA_STATE' | 'INTER_STATE';
  lines: PreviewLine[];
  subtotal: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalGST: number;
  totalAmount: number;
  accounting: AccountingPreviewLine[];
  balanced: boolean;
}

const InvoiceFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const salesOrderIdParam = searchParams.get('salesOrderId');
  const deliveryIdParam = searchParams.get('deliveryId');
  const c = useControlRoomColors();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loadingRefData, setLoadingRefData] = useState(true);

  const [soRef, setSoRef] = useState<SalesOrderRef | null>(null);
  const [deliveryRef, setDeliveryRef] = useState<DeliveryRef | null>(null);
  const [loadingSourceDoc, setLoadingSourceDoc] = useState(!!(salesOrderIdParam || deliveryIdParam));

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [customerPoNumber, setCustomerPoNumber] = useState('');
  const [customerPoDate, setCustomerPoDate] = useState('');
  const [creditDays, setCreditDays] = useState('30');
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [charges, setCharges] = useState<ChargeDraft[]>([]);
  const [notes, setNotes] = useState('');

  const [tdsApplicable, setTdsApplicable] = useState(false);
  const [tdsSection, setTdsSection] = useState('');
  const [tdsRate, setTdsRate] = useState('');

  const [stockByProduct, setStockByProduct] = useState<Record<string, number>>({});
  const [creditStatus, setCreditStatus] = useState<{ status: string; outstanding: number; projectedExposure: number; availableCredit: number } | null>(null);

  // Real server tax + accounting preview (POST /sales/invoices/preview), debounced.
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Captured once, right after a Sales-Order pre-fill, so we can tell later
  // whether the lines/customer are still an exact copy of the order (in
  // which case saving posts the order's own lines via createInvoiceFromOrder)
  // or have been edited (in which case it's a manual, item-by-item create).
  const originalOrderSignatureRef = useRef<string | null>(null);
  const originalOrderCustomerIdRef = useRef<string | null>(null);
  // Same tracking, for the Delivery hand-off (POST /deliveries/:id/invoice).
  const originalDeliverySignatureRef = useRef<string | null>(null);
  const originalDeliveryCustomerIdRef = useRef<string | null>(null);
  // Structured 422 CREDIT_LIMIT_EXCEEDED payload from Sales.assertCreditLimit,
  // shown as an exception banner with an explicit "Create Anyway" override
  // instead of a bare error string.
  const [creditException, setCreditException] = useState<CreditLimitExceededDetails | null>(null);

  const isFromSalesOrder = !!soRef;
  // Both customer and line items stay editable after a Sales Order / Delivery
  // hand-off — see useOrderBasedCreate below, which is what actually decides
  // which create endpoint to call at submit time.
  const customerLocked = false;
  const itemsEditable = true;

  // Reference data: customers, products, company settings (seller state for GST split)
  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      customerService.getCustomers().catch(() => [] as Customer[]),
      productService.getProducts().catch(() => ({ products: [] as Product[], count: 0 })),
      invoiceService.getCompanySettings().catch(() => null),
    ])
      .then(([custs, prodRes, settings]) => {
        setCustomers(custs);
        setProducts(prodRes.products || []);
        setCompanySettings(settings);
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  // Prefill from Sales Order (whole order — partial billing isn't supported by this backend yet)
  useEffect(() => {
    if (!salesOrderIdParam || loadingRefData) return;
    setLoadingSourceDoc(true);
    api
      .get<{ salesOrder: SalesOrderRef }>(`/sales/orders/${salesOrderIdParam}`)
      .then(({ salesOrder }) => {
        setSoRef(salesOrder);
        const matchedCustomer = customers.find((cu) => String(cu.id) === String(salesOrder.customerId)) || null;
        setCustomer(matchedCustomer);
        originalOrderCustomerIdRef.current = salesOrder.customerId != null ? String(salesOrder.customerId) : null;

        const draftItems: ItemDraft[] = (salesOrder.items || []).map((it) => {
          const pid = it.productId ?? it.product_id;
          const prod = products.find((p) => String(p.id) === String(pid)) || null;
          return {
            key: nextDraftKey(),
            product: prod,
            hsnCode: prod?.hsn_code || '',
            quantity: String(toNum(it.quantity) || 1),
            unitPrice: String(toNum(it.unitPrice ?? it.unit_price) || prod?.base_price || 0),
            discountPercent: '0',
            gstRate: String(prod?.gst_rate ?? 18),
            priceOverridden: false,
          };
        });
        setItems(draftItems);

        // Snapshot the pre-filled signature — as long as it matches the
        // current lines/customer at submit time, we bill the order directly
        // instead of re-sending edited lines manually.
        originalOrderSignatureRef.current = draftItems
          .filter((it) => it.product)
          .map((it) => itemSig(it.product!.id, toNum(it.quantity), toNum(it.unitPrice), toNum(it.discountPercent)))
          .join('|');
      })
      .catch((err: any) => setError(err.message || 'Failed to load the referenced Sales Order'))
      .finally(() => setLoadingSourceDoc(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesOrderIdParam, loadingRefData]);

  // Prefill from Delivery. The backend now has a dedicated copy endpoint
  // (POST /deliveries/:id/invoice — Sales.createInvoiceFromDelivery, which
  // resolves warehouse/batch server-side and guards double-billing), used
  // below at submit time as long as the pre-filled lines/customer are still
  // an exact copy — the same unedited-vs-edited pattern as the Sales Order
  // hand-off.
  useEffect(() => {
    if (!deliveryIdParam || salesOrderIdParam || loadingRefData) return;
    setLoadingSourceDoc(true);
    api
      .get<{ delivery: DeliveryRef }>(`/deliveries/${deliveryIdParam}`)
      .then(({ delivery }) => {
        setDeliveryRef(delivery);
        const matchedCustomer = customers.find((cu) => String(cu.id) === String(delivery.customer_id)) || null;
        setCustomer(matchedCustomer);
        originalDeliveryCustomerIdRef.current = delivery.customer_id != null ? String(delivery.customer_id) : null;

        const draftItems: ItemDraft[] = (delivery.items || []).map((it) => {
          const prod = products.find((p) => String(p.id) === String(it.product_id)) || null;
          return {
            key: nextDraftKey(),
            product: prod,
            hsnCode: prod?.hsn_code || '',
            quantity: String(toNum(it.delivered_qty ?? it.quantity) || 1),
            unitPrice: String(prod?.base_price ?? 0),
            discountPercent: '0',
            gstRate: String(prod?.gst_rate ?? 18),
            priceOverridden: false,
          };
        });
        setItems(draftItems);
        originalDeliverySignatureRef.current = draftItems
          .filter((it) => it.product)
          .map((it) => itemSig(it.product!.id, toNum(it.quantity), toNum(it.unitPrice), toNum(it.discountPercent)))
          .join('|');
      })
      .catch((err: any) => setError(err.message || 'Failed to load the referenced Delivery'))
      .finally(() => setLoadingSourceDoc(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryIdParam, salesOrderIdParam, loadingRefData]);

  // Keep payment terms in step with whichever customer is active
  useEffect(() => {
    if (customer) setCreditDays(String(customer.credit_days ?? 30));
  }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real per-line stock check — GET /inventory/stock/product/:productId
  useEffect(() => {
    const ids = Array.from(
      new Set(items.map((it) => it.product?.id).filter((id): id is string => !!id && stockByProduct[id] === undefined))
    );
    if (ids.length === 0) return;
    let alive = true;
    Promise.all(
      ids.map((id) =>
        api
          .get<any>(`/inventory/stock/product/${id}`)
          .then((res) => ({ id, qty: Number(res?.stock?.quantity ?? res?.quantity ?? res?.availableQty ?? NaN) }))
          .catch(() => ({ id, qty: NaN }))
      )
    ).then((results) => {
      if (!alive) return;
      setStockByProduct((prev) => {
        const next = { ...prev };
        results.forEach((r) => {
          if (!Number.isNaN(r.qty)) next[r.id] = r.qty;
        });
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [items, stockByProduct]);

  // Real server tax + accounting preview — POST /sales/invoices/preview,
  // debounced ~400ms. Runs off (customerId, items) only: additional charges
  // and TDS aren't modelled by that endpoint, so they stay client-side-only
  // (see the Accounting Preview panel's disclaimer below).
  const itemsSignature = useMemo(
    () => items.map((it) => `${it.product?.id ?? ''}:${it.quantity}:${it.unitPrice}:${it.discountPercent}:${it.gstRate}`).join('|'),
    [items]
  );
  useEffect(() => {
    if (!customer) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const validItems = items.filter((it) => it.product && toNum(it.quantity) > 0 && toNum(it.unitPrice) > 0);
    if (validItems.length === 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const { preview: p } = await api.post<{ preview: InvoicePreview }>('/sales/invoices/preview', {
          customerId: customer.id,
          items: validItems.map((it) => ({
            productId: Number(it.product!.id),
            quantity: toNum(it.quantity),
            unitPrice: toNum(it.unitPrice),
            discountPercent: toNum(it.discountPercent),
            gstRate: toNum(it.gstRate),
          })),
        });
        if (!cancelled) setPreview(p);
      } catch (err: any) {
        if (!cancelled) setPreviewError(err.message || 'Failed to compute the tax preview.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, itemsSignature]);

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (key: string) => setItems((prev) => prev.filter((it) => it.key !== key));
  const updateItem = (key: string, patch: Partial<ItemDraft>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const handleProductChange = (key: string, product: Product | null) => {
    setItems((prev) =>
      prev.map((it) =>
        it.key !== key
          ? it
          : {
              ...it,
              product,
              hsnCode: product?.hsn_code || it.hsnCode,
              unitPrice: product ? String(product.base_price ?? 0) : it.unitPrice,
              gstRate: product ? String(product.gst_rate ?? 18) : it.gstRate,
              priceOverridden: false,
            }
      )
    );
  };

  const addCharge = () => setCharges((prev) => [...prev, emptyCharge()]);
  const removeCharge = (key: string) => setCharges((prev) => prev.filter((ch) => ch.key !== key));
  const updateCharge = (key: string, patch: Partial<ChargeDraft>) =>
    setCharges((prev) => prev.map((ch) => (ch.key === key ? { ...ch, ...patch } : ch)));

  const companyState = companySettings?.state || null;
  const customerState = customer?.state || (customer?.gstin ? getStateFromGSTIN(customer.gstin) : null);
  const overallGstType = determineGstType(companyState, customerState);

  const lineGst = (it: ItemDraft): GstBreakup =>
    calculateLineGst(toNum(it.quantity), toNum(it.unitPrice), toNum(it.discountPercent), toNum(it.gstRate), companyState, customerState);

  const chargeGst = (ch: ChargeDraft): GstBreakup =>
    ch.taxable
      ? calculateLineGst(1, toNum(ch.amount), 0, toNum(ch.gstRate), companyState, customerState)
      : {
          taxableAmount: toNum(ch.amount),
          cgstRate: 0,
          sgstRate: 0,
          igstRate: 0,
          cgstAmount: 0,
          sgstAmount: 0,
          igstAmount: 0,
          totalGstAmount: 0,
          totalAmount: toNum(ch.amount),
          gstType: 'EXEMPT',
        };

  // Maps each editable line to its matching server preview line (same order
  // the request was sent in), so the grid's read-only tax cells show the
  // genuine server calculation once it lands, not just the client estimate.
  const previewLineByKey = useMemo(() => {
    const map = new Map<string, PreviewLine>();
    if (!preview) return map;
    let li = 0;
    items.forEach((it) => {
      if (it.product && toNum(it.quantity) > 0 && toNum(it.unitPrice) > 0) {
        const line = preview.lines[li];
        li += 1;
        if (line) map.set(it.key, line);
      }
    });
    return map;
  }, [preview, items]);

  const totals = useMemo(() => {
    let taxable = 0,
      cgst = 0,
      sgst = 0,
      igst = 0;
    items.forEach((it) => {
      if (!it.product && !isFromSalesOrder) return;
      const g = lineGst(it);
      taxable += g.taxableAmount;
      cgst += g.cgstAmount;
      sgst += g.sgstAmount;
      igst += g.igstAmount;
    });
    let chargesTaxable = 0,
      chargesCgst = 0,
      chargesSgst = 0,
      chargesIgst = 0;
    charges.forEach((ch) => {
      const g = chargeGst(ch);
      chargesTaxable += g.taxableAmount;
      chargesCgst += g.cgstAmount;
      chargesSgst += g.sgstAmount;
      chargesIgst += g.igstAmount;
    });
    const subtotal = round2(taxable + chargesTaxable);
    const totalCgst = round2(cgst + chargesCgst);
    const totalSgst = round2(sgst + chargesSgst);
    const totalIgst = round2(igst + chargesIgst);
    const preRound = subtotal + totalCgst + totalSgst + totalIgst;
    const grandTotal = Math.round(preRound);
    const roundOff = round2(grandTotal - preRound);
    const tdsAmount = tdsApplicable && tdsRate ? round2((grandTotal * toNum(tdsRate)) / 100) : 0;
    const netReceivable = round2(grandTotal - tdsAmount);
    return { taxable: round2(taxable), chargesTaxable: round2(chargesTaxable), subtotal, totalCgst, totalSgst, totalIgst, preRound, grandTotal, roundOff, tdsAmount, netReceivable };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, charges, companyState, customerState, tdsApplicable, tdsRate, isFromSalesOrder]);

  const dueDate = useMemo(() => {
    if (!invoiceDate) return '';
    const d = new Date(invoiceDate);
    d.setDate(d.getDate() + (toNum(creditDays) || 0));
    return d.toISOString().slice(0, 10);
  }, [invoiceDate, creditDays]);

  const liveChecks: CheckResult[] = useMemo(() => {
    const checks: CheckResult[] = [];
    checks.push({ label: 'Customer selected', ok: !!customer });
    if (customer) {
      checks.push({ label: 'Customer active', ok: customer.is_active !== false });
      const gc = customer.gstin ? validateGSTIN(customer.gstin) : null;
      checks.push({
        label: 'GSTIN valid',
        ok: customer.gstin ? !!gc?.valid : 'warn',
        detail: customer.gstin ? gc?.error : 'No GSTIN on file for this customer',
      });
      checks.push({ label: 'Place of Supply available', ok: !!customerState, detail: customerState ? undefined : 'No state on customer record' });
    }
    const realItems = items.filter((it) => it.product);
    checks.push({ label: 'At least one line item', ok: realItems.length > 0 });
    if (itemsEditable) {
      items.forEach((it, idx) => {
        if (!it.product) return;
        checks.push({ label: `Line ${idx + 1}: product active`, ok: it.product.is_active !== false });
        checks.push({ label: `Line ${idx + 1}: HSN/SAC available`, ok: !!it.hsnCode });
        checks.push({ label: `Line ${idx + 1}: quantity > 0`, ok: toNum(it.quantity) > 0 });
        const avail = stockByProduct[String(it.product.id)];
        if (avail !== undefined) {
          checks.push({
            label: `Line ${idx + 1}: stock available`,
            ok: avail >= toNum(it.quantity) ? true : 'warn',
            detail: avail < toNum(it.quantity) ? `Only ${avail} in stock, ${toNum(it.quantity) - avail} short` : undefined,
          });
        }
        if (toNum(it.discountPercent) > 15) {
          checks.push({ label: `Line ${idx + 1}: discount within limit`, ok: 'warn', detail: `${it.discountPercent}% may require approval (advisory threshold)` });
        }
      });
    }
    checks.push({ label: 'Invoice date valid', ok: !!invoiceDate });
    checks.push({
      label: 'Credit available',
      ok: creditStatus ? (creditStatus.status !== 'EXCEEDED' ? true : 'warn') : 'warn',
      detail: creditStatus?.status === 'EXCEEDED' ? 'Credit limit exceeded — approval required to proceed' : undefined,
    });
    return checks;
  }, [customer, customerState, items, itemsEditable, invoiceDate, creditStatus, stockByProduct]);

  const hardBlockErrors = liveChecks.filter((chk) => chk.ok === false).map((chk) => chk.label);

  // The order-based create endpoint (POST /sales/orders/:id/invoice) posts
  // the ORDER'S OWN lines server-side — whatever we send it besides
  // invoiceDate is ignored. So it's only correct to use while the pre-filled
  // lines and customer are still an exact, unedited copy of the order; any
  // edit must go through the manual create instead, with the real items.
  const currentOrderSignature = useMemo(
    () =>
      items
        .filter((it) => it.product)
        .map((it) => itemSig(it.product!.id, toNum(it.quantity), toNum(it.unitPrice), toNum(it.discountPercent)))
        .join('|'),
    [items]
  );
  const useOrderBasedCreate =
    isFromSalesOrder &&
    !!soRef &&
    originalOrderSignatureRef.current !== null &&
    currentOrderSignature === originalOrderSignatureRef.current &&
    !!customer &&
    originalOrderCustomerIdRef.current !== null &&
    String(customer.id) === originalOrderCustomerIdRef.current;

  // Same unedited-copy check for the Delivery hand-off, against the real
  // POST /deliveries/:id/invoice endpoint.
  const useDeliveryBasedCreate =
    !!deliveryRef &&
    !isFromSalesOrder &&
    originalDeliverySignatureRef.current !== null &&
    currentOrderSignature === originalDeliverySignatureRef.current &&
    !!customer &&
    originalDeliveryCustomerIdRef.current !== null &&
    String(customer.id) === originalDeliveryCustomerIdRef.current;

  const submit = async (override: boolean) => {
    const errs = [...hardBlockErrors];
    if (charges.some((ch) => toNum(ch.amount) > 0)) {
      errs.push('Additional charges are shown for planning only — this backend does not yet persist them as invoice lines. Remove them, or capture them in Notes instead.');
    }
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    setCreditException(null);
    try {
      let created: any;
      const tdsFields = tdsApplicable
        ? { tdsApplicable: true, tdsSectionCode: tdsSection || undefined, tdsRate: toNum(tdsRate) }
        : {};
      if (useOrderBasedCreate && soRef) {
        created = await invoiceService.createInvoiceFromOrder(soRef.id, {
          invoiceDate,
          paymentTerms: toNum(creditDays) || 30,
          overrideCreditCheck: override || undefined,
          ...tdsFields,
        });
      } else if (useDeliveryBasedCreate && deliveryRef) {
        created = await invoiceService.createInvoiceFromDelivery(deliveryRef.id, {
          invoiceDate,
          paymentTerms: toNum(creditDays) || 30,
          overrideCreditCheck: override || undefined,
          ...tdsFields,
        });
      } else {
        const composedNotes = [
          customerPoNumber && `Customer PO: ${customerPoNumber}`,
          customerPoDate && `PO Date: ${customerPoDate}`,
          deliveryRef && `Delivery: ${deliveryRef.delivery_number}`,
          notes,
        ]
          .filter(Boolean)
          .join(' | ');
        created = await invoiceService.createInvoice({
          customerId: customer!.id,
          invoiceDate,
          paymentTerms: toNum(creditDays) || 30,
          items: items
            .filter((it) => it.product)
            .map((it) => ({
              productId: Number(it.product!.id),
              quantity: toNum(it.quantity),
              unitPrice: toNum(it.unitPrice),
              discountPercent: toNum(it.discountPercent),
              gstRate: toNum(it.gstRate),
            })),
          notes: composedNotes || undefined,
          overrideCreditCheck: override || undefined,
          ...tdsFields,
        });
      }
      navigate(`/sales/invoices/${created.id}`);
    } catch (err: any) {
      const details = err?.response?.data?.details;
      if (err?.response?.data?.code === 'CREDIT_LIMIT_EXCEEDED' && details) {
        setCreditException(details as CreditLimitExceededDetails);
      } else {
        setError(err.message || 'Failed to create invoice');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => submit(false);
  const handleOverrideAndCreate = () => submit(true);

  if (loadingRefData || loadingSourceDoc) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 12 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/invoices')} sx={{ mb: 1 }}>
        Back to Invoices
      </Button>

      <PageHeader
        title="New Sales Invoice"
        subtitle="Indian GST tax invoice — customer, tax and credit details are pulled from masters, not retyped."
        actions={<Chip size="small" variant="outlined" label="Invoice No: auto-generated on save" />}
      />

      {(soRef || deliveryRef) && (
        <Paper elevation={0} sx={{ p: 1.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            SOURCE CHAIN:
          </Typography>
          {soRef?.related?.enquiry && (
            <Chip size="small" label={`Enquiry #${soRef.related.enquiry.id}`} onClick={() => navigate(`/sales/enquiries/${soRef.related!.enquiry!.id}`)} clickable />
          )}
          {soRef?.related?.contract && (
            <Chip size="small" label={`Contract #${soRef.related.contract.id}`} onClick={() => navigate(`/sales/contracts/${soRef.related!.contract!.id}`)} clickable />
          )}
          {soRef && (
            <>
              <Typography variant="caption" color="text.disabled">→</Typography>
              <Chip size="small" color="primary" label={soRef.orderNumber} onClick={() => navigate(`/sales/orders/${soRef.id}`)} clickable />
            </>
          )}
          {deliveryRef && (
            <>
              <Typography variant="caption" color="text.disabled">→</Typography>
              <Chip size="small" color="primary" label={deliveryRef.delivery_number} onClick={() => navigate(`/sales/deliveries/${deliveryRef.id}`)} clickable />
            </>
          )}
          <Typography variant="caption" color="text.disabled">→</Typography>
          <Chip size="small" label="This Invoice" />
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isFromSalesOrder && (
        <Alert severity={useOrderBasedCreate ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {useOrderBasedCreate ? (
            <>
              Pre-filled from Sales Order <b>{soRef?.orderNumber}</b>. Saving as-is bills the remaining order
              directly. Edit any line or the customer and this switches to a manual invoice instead — it will still
              be created, just not linked back to this order's own line-level billed-quantity tracking.
            </>
          ) : (
            <>
              Lines have been edited from Sales Order <b>{soRef?.orderNumber}</b> — this will now save as a manual
              invoice, not linked back to the order's line-level billing.
            </>
          )}
        </Alert>
      )}

      {deliveryRef && !isFromSalesOrder && (
        <Alert severity={useDeliveryBasedCreate ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {useDeliveryBasedCreate ? (
            <>
              Pre-filled from Delivery <b>{deliveryRef.delivery_number}</b>. Saving as-is bills this delivery
              directly, with warehouse/batch resolved server-side. Edit any line or the customer and this switches
              to a manual invoice instead.
            </>
          ) : (
            <>
              Lines have been edited from Delivery <b>{deliveryRef.delivery_number}</b> — this will now save as a
              manual invoice, not linked back to the delivery.
            </>
          )}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      {creditException && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setCreditException(null)}
          action={
            <Button color="inherit" size="small" variant="outlined" onClick={handleOverrideAndCreate} disabled={submitting}>
              Create Anyway
            </Button>
          }
        >
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>⚠ CREDIT LIMIT EXCEEDED</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2">Credit Limit: {formatIndianNumber(creditException.creditLimit)}</Typography>
            <Typography variant="body2">Outstanding: {formatIndianNumber(creditException.outstanding)}</Typography>
            <Typography variant="body2">Current Invoice: {formatIndianNumber(creditException.currentInvoiceAmount)}</Typography>
            <Typography variant="body2">Projected: {formatIndianNumber(creditException.projectedExposure)}</Typography>
            <Typography variant="body2" fontWeight={700}>Excess: {formatIndianNumber(creditException.excess)}</Typography>
          </Box>
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 380px' }, gap: 2.5, alignItems: 'start' }}>
        {/* ================= MAIN WORKSPACE ================= */}
        <Box sx={{ minWidth: 0 }}>
          {/* Document Details */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Document Details
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Invoice Type" value="Tax Invoice (B2B/B2C)" disabled />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Invoice Date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Salesperson / Territory" value="Not configured on customer master" disabled />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Customer PO Number"
                  value={customerPoNumber}
                  onChange={(e) => setCustomerPoNumber(e.target.value)}
                  placeholder="Optional"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Customer PO Date"
                  value={customerPoDate}
                  onChange={(e) => setCustomerPoDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Customer & Tax Details */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Customer &amp; Tax Details
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                {customerLocked ? (
                  <TextField
                    fullWidth
                    size="small"
                    label="Customer"
                    value={customer ? `${customer.company_name} (${customer.customer_code})` : 'Loading…'}
                    disabled
                    helperText={`Locked — set by the ${isFromSalesOrder ? 'Sales Order' : 'Delivery'} this invoice was created from`}
                  />
                ) : (
                  <Autocomplete
                    size="small"
                    options={customers}
                    value={customer}
                    onChange={(_, v) => setCustomer(v)}
                    getOptionLabel={(o) => `${o.company_name} (${o.customer_code})`}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{option.company_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.customer_code} · {option.state || 'No state on file'} · GSTIN: {option.gstin || 'N/A'}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Customer *" placeholder="Search by name or code" />}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Credit Days"
                  value={creditDays}
                  onChange={(e) => setCreditDays(e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField fullWidth size="small" label="Due Date" value={dueDate} disabled helperText="Invoice Date + Credit Days" />
              </Grid>

              {customer && (
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">GSTIN</Typography>
                        <Typography variant="body2" fontFamily="monospace">{customer.gstin || 'Not on file'}</Typography>
                        {customer.gstin && (
                          validateGSTIN(customer.gstin).valid ? (
                            <Chip size="small" sx={{ mt: 0.5 }} icon={<CheckCircle sx={{ fontSize: 14 }} />} label="GSTIN Valid" color="success" variant="outlined" />
                          ) : (
                            <Chip size="small" sx={{ mt: 0.5 }} icon={<ErrorOutline sx={{ fontSize: 14 }} />} label="Invalid GSTIN format" color="error" variant="outlined" />
                          )
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">PAN</Typography>
                        <Typography variant="body2">Not on file</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Place of Supply</Typography>
                        <Typography variant="body2">{customerState || '—'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Tax Treatment</Typography>
                        <Typography variant="body2">{customer.gstin ? 'Regular (GST Registered)' : 'Not registered on file'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Billing Address</Typography>
                        <Typography variant="body2">{customer.city && customer.state ? `${customer.city}, ${customer.state}` : customer.address || '—'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Shipping Address</Typography>
                        <Typography variant="body2">Same as billing (no separate shipping address on file)</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Items */}
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2.5 }}>
            <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Line Items
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  size="small"
                  label={
                    preview
                      ? preview.taxType === 'INTER_STATE'
                        ? 'Inter-State (IGST) — server-verified'
                        : 'Intra-State (CGST+SGST) — server-verified'
                      : overallGstType === 'IGST'
                      ? 'Inter-State (IGST)'
                      : overallGstType === 'CGST_SGST'
                      ? 'Intra-State (CGST+SGST)'
                      : 'No state data — exempt preview'
                  }
                  color={(preview ? preview.taxType === 'INTER_STATE' : overallGstType === 'IGST') ? 'warning' : overallGstType === 'EXEMPT' && !preview ? 'default' : 'success'}
                />
                {itemsEditable && (
                  <Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={addItem}>
                    Add Item
                  </Button>
                )}
              </Box>
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>#</TableCell>
                    <TableCell sx={headerCellSx}>Product</TableCell>
                    <TableCell sx={headerCellSx}>HSN/SAC</TableCell>
                    <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                    <TableCell sx={headerCellSx} align="right">Rate</TableCell>
                    <TableCell sx={headerCellSx} align="right">Disc %</TableCell>
                    <TableCell sx={headerCellSx} align="right">Taxable</TableCell>
                    <TableCell sx={headerCellSx} align="right">CGST</TableCell>
                    <TableCell sx={headerCellSx} align="right">SGST</TableCell>
                    <TableCell sx={headerCellSx} align="right">IGST</TableCell>
                    <TableCell sx={headerCellSx} align="right">Line Total</TableCell>
                    {itemsEditable && <TableCell sx={headerCellSx} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it, idx) => {
                    const serverLine = previewLineByKey.get(it.key);
                    // Prefer the real server-computed split; fall back to the
                    // instant client mirror only until that request resolves.
                    const g: GstBreakup = serverLine
                      ? {
                          taxableAmount: serverLine.taxableAmount,
                          cgstRate: 0,
                          sgstRate: 0,
                          igstRate: 0,
                          cgstAmount: serverLine.cgstAmount,
                          sgstAmount: serverLine.sgstAmount,
                          igstAmount: serverLine.igstAmount,
                          totalGstAmount: round2(serverLine.cgstAmount + serverLine.sgstAmount + serverLine.igstAmount),
                          totalAmount: serverLine.lineTotal,
                          gstType: overallGstType,
                        }
                      : lineGst(it);
                    const stock = it.product ? stockByProduct[String(it.product.id)] : undefined;
                    const shortfall = stock !== undefined && stock < toNum(it.quantity);
                    return (
                      <TableRow key={it.key} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          {itemsEditable ? (
                            <>
                              <Autocomplete
                                size="small"
                                options={products}
                                value={it.product}
                                onChange={(_, v) => handleProductChange(it.key, v)}
                                getOptionLabel={(o) => `${o.product_name} (${o.product_code})`}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.id}>
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>{option.product_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {option.product_code} · HSN {option.hsn_code || '—'} · GST {option.gst_rate}% · {formatCurrency(option.base_price || 0)}/{option.unit_of_measure}
                                      </Typography>
                                    </Box>
                                  </li>
                                )}
                                renderInput={(params) => <TextField {...params} placeholder="Select product" />}
                              />
                              {it.product && (
                                <Typography variant="caption" color={it.priceOverridden ? 'warning.main' : 'text.secondary'} sx={{ display: 'block', mt: 0.25 }}>
                                  {it.priceOverridden ? '⚠ Manually overridden' : 'Price from Product Master'}
                                </Typography>
                              )}
                              {shortfall && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
                                  ⚠ Only {stock} in stock ({toNum(it.quantity) - (stock || 0)} short)
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" fontWeight={600}>{it.product?.product_name || '—'}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 90 }}>
                          <Typography variant="body2" fontFamily="monospace">{it.hsnCode || '—'}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 80 }}>
                          {itemsEditable ? (
                            <TextField
                              size="small"
                              type="number"
                              value={it.quantity}
                              onChange={(e) => updateItem(it.key, { quantity: e.target.value })}
                              inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                              sx={{ minWidth: 70 }}
                            />
                          ) : (
                            <Typography variant="body2">{it.quantity}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          {itemsEditable ? (
                            <TextField
                              size="small"
                              type="number"
                              value={it.unitPrice}
                              onChange={(e) => updateItem(it.key, { unitPrice: e.target.value, priceOverridden: it.product ? Number(e.target.value) !== it.product.base_price : false })}
                              inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                              sx={{ minWidth: 90 }}
                            />
                          ) : (
                            <Typography variant="body2">{formatCurrency(toNum(it.unitPrice))}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 80 }}>
                          {itemsEditable ? (
                            <TextField
                              size="small"
                              type="number"
                              value={it.discountPercent}
                              onChange={(e) => updateItem(it.key, { discountPercent: e.target.value })}
                              inputProps={{ min: 0, max: 100, step: '0.01', style: { textAlign: 'right' } }}
                              sx={{ minWidth: 70 }}
                            />
                          ) : (
                            <Typography variant="body2">{it.discountPercent}%</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(g.taxableAmount)}</TableCell>
                        <TableCell align="right">{g.cgstAmount ? formatCurrency(g.cgstAmount) : '—'}</TableCell>
                        <TableCell align="right">{g.sgstAmount ? formatCurrency(g.sgstAmount) : '—'}</TableCell>
                        <TableCell align="right">{g.igstAmount ? formatCurrency(g.igstAmount) : '—'}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>{formatCurrency(g.totalAmount)}</Typography>
                        </TableCell>
                        {itemsEditable && (
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => removeItem(it.key)} disabled={items.length === 1}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Additional Charges */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Additional Charges (Freight / Packing / Insurance / Other)
              </Typography>
              <Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={addCharge}>
                Add Charge
              </Button>
            </Box>
            {charges.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No additional charges on this invoice.</Typography>
            ) : (
              <>
                {charges.map((ch) => (
                  <Grid container spacing={1.5} key={ch.key} sx={{ mb: 1, alignItems: 'center' }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth size="small" label="Description" value={ch.description} onChange={(e) => updateCharge(ch.key, { description: e.target.value })} placeholder="e.g. Freight" />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <TextField fullWidth size="small" type="number" label="Amount" value={ch.amount} onChange={(e) => updateCharge(ch.key, { amount: e.target.value })} inputProps={{ min: 0 }} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                      <TextField fullWidth size="small" select label="GST %" value={ch.gstRate} onChange={(e) => updateCharge(ch.key, { gstRate: e.target.value })} disabled={!ch.taxable}>
                        {GST_RATE_OPTIONS.map((r) => (
                          <MenuItem key={r} value={r}>{r}%</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 8, sm: 3 }}>
                      <FormControlLabel control={<Checkbox size="small" checked={ch.taxable} onChange={(e) => updateCharge(ch.key, { taxable: e.target.checked })} />} label="Taxable" />
                    </Grid>
                    <Grid size={{ xs: 4, sm: 1 }}>
                      <IconButton size="small" color="error" onClick={() => removeCharge(ch.key)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Shown here for pricing/planning only — this backend does not yet persist additional charges as ledger-mapped invoice lines. Remove them before creating the invoice, or capture them in Notes.
                </Alert>
              </>
            )}
          </Paper>

          {/* TDS */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <FormControlLabel
              control={<Checkbox checked={tdsApplicable} onChange={(e) => setTdsApplicable(e.target.checked)} />}
              label={<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>TDS / Other Withholding Applicable</Typography>}
            />
            {tdsApplicable && (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" label="TDS Section" value={tdsSection} onChange={(e) => setTdsSection(e.target.value)} placeholder="e.g. 194Q / 206C(1H)" />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" type="number" label="TDS Rate %" value={tdsRate} onChange={(e) => setTdsRate(e.target.value)} inputProps={{ min: 0, max: 100, step: '0.01' }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" label="TDS Amount (preview)" value={formatCurrency(totals.tdsAmount)} disabled />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">
                    Captured in the invoice Notes either way; posts as a real GL entry only where the backend's TDS-on-sales support is configured.
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Paper>

          {/* Shipping & Dispatch — only when this invoice comes from a real Delivery record */}
          {deliveryRef && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Shipping &amp; Dispatch <Typography component="span" variant="caption" color="text.secondary">(from {deliveryRef.delivery_number})</Typography>
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary" display="block">Warehouse</Typography><Typography variant="body2">{deliveryRef.warehouse_name || '—'}</Typography></Grid>
                <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary" display="block">Transporter</Typography><Typography variant="body2">{deliveryRef.transporter || '—'}</Typography></Grid>
                <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary" display="block">Transport Mode</Typography><Typography variant="body2">{deliveryRef.transport_mode || '—'}</Typography></Grid>
                <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary" display="block">Vehicle Number</Typography><Typography variant="body2">{deliveryRef.vehicle_no || '—'}</Typography></Grid>
                <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary" display="block">Dispatch From</Typography><Typography variant="body2">{deliveryRef.dispatch_from || '—'}</Typography></Grid>
                <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary" display="block">Deliver To</Typography><Typography variant="body2">{deliveryRef.deliver_to || '—'}</Typography></Grid>
                <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary" display="block">Distance</Typography><Typography variant="body2">{deliveryRef.distance_km ? `${deliveryRef.distance_km} km` : '—'}</Typography></Grid>
              </Grid>
            </Paper>
          )}

          {/* Notes */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Notes
            </Typography>
            <TextField fullWidth size="small" multiline minRows={3} label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes or terms visible on the invoice" />
          </Paper>
        </Box>

        {/* ================= SIDEBAR ================= */}
        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 88 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Invoice / Tax Summary */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Invoice Summary
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary">Taxable Value (Items)</Typography>
              <Typography variant="body2" fontWeight={600}>{formatCurrency(totals.taxable)}</Typography>
            </Box>
            {totals.chargesTaxable > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">Additional Charges</Typography>
                <Typography variant="body2" fontWeight={600}>{formatCurrency(totals.chargesTaxable)}</Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            {overallGstType !== 'IGST' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">CGST</Typography>
                  <Typography variant="body2">{formatCurrency(totals.totalCgst)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">SGST</Typography>
                  <Typography variant="body2">{formatCurrency(totals.totalSgst)}</Typography>
                </Box>
              </>
            )}
            {overallGstType === 'IGST' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">IGST</Typography>
                <Typography variant="body2">{formatCurrency(totals.totalIgst)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary">Round Off</Typography>
              <Typography variant="body2">{formatCurrency(totals.roundOff)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight={700}>Grand Total</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: c.accent }}>{formatCurrency(totals.grandTotal)}</Typography>
            </Box>
            {tdsApplicable && totals.tdsAmount > 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Less: TDS</Typography>
                  <Typography variant="body2" color="error.main">−{formatCurrency(totals.tdsAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight={700}>Net Receivable</Typography>
                  <Typography variant="body2" fontWeight={700}>{formatCurrency(totals.netReceivable)}</Typography>
                </Box>
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Preview — the server recomputes and persists the authoritative figures on save.
            </Typography>
          </Paper>

          <CreditStatusCard customer={customer} invoiceAmount={totals.grandTotal} onCreditStatusChange={setCreditStatus} />

          {/* Accounting Preview — real GL lines from POST /sales/invoices/preview
              (Sales.previewInvoice), i.e. actual chart-of-accounts code + name
              and a genuine balanced check, not hardcoded category labels. */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                Accounting Preview
              </Typography>
              {previewLoading && <CircularProgress size={14} />}
            </Box>
            {!customer ? (
              <Typography variant="body2" color="text.secondary">
                Select a customer and add line items to preview the GL posting.
              </Typography>
            ) : previewError ? (
              <Typography variant="body2" color="error.main">{previewError}</Typography>
            ) : preview ? (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {preview.accounting.map((a) => (
                    <Box key={a.code} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" component="span" sx={{ fontFamily: 'inherit' }}>
                        {a.debit > 0 ? 'Dr' : '  Cr'} {a.code} {a.name}
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                        {formatCurrency(a.debit > 0 ? a.debit : a.credit)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Chip
                  size="small"
                  sx={{ mt: 1.5, fontWeight: 700 }}
                  label={preview.balanced ? 'Balanced' : 'Not Balanced'}
                  color={preview.balanced ? 'success' : 'error'}
                  variant="outlined"
                />
                {(charges.length > 0 || tdsApplicable) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Items only — this real preview excludes the additional charges and TDS shown above, which the
                    invoice posting endpoint does not yet model.
                  </Typography>
                )}
                {items.some((it) => it.product) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Stock items also post Dr COGS / Cr Inventory from real cost layers — computed server-side at posting time.
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Add a product line with quantity and rate to preview the GL posting.
              </Typography>
            )}
          </Paper>

          {/* AR Impact */}
          {creditStatus && (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                AR Impact
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">Current Outstanding</Typography>
                <Typography variant="body2">{formatIndianNumber(creditStatus.outstanding)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">+ This Invoice</Typography>
                <Typography variant="body2">{formatIndianNumber(totals.grandTotal)}</Typography>
              </Box>
              {tdsApplicable && totals.tdsAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">− TDS</Typography>
                  <Typography variant="body2">{formatIndianNumber(totals.tdsAmount)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={700}>Projected Outstanding</Typography>
                <Typography variant="body2" fontWeight={700}>{formatIndianNumber(creditStatus.projectedExposure)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                <Typography variant="body2" color="text.secondary">Due Date</Typography>
                <Typography variant="body2">{dueDate}</Typography>
              </Box>
            </Paper>
          )}

          {/* Validation Status */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Validation Status
            </Typography>
            {liveChecks.map((chk) => (
              <Box key={chk.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
                {chk.ok === true ? (
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main', mt: 0.15 }} />
                ) : chk.ok === 'warn' ? (
                  <WarningAmber sx={{ fontSize: 16, color: 'warning.main', mt: 0.15 }} />
                ) : (
                  <ErrorOutline sx={{ fontSize: 16, color: 'error.main', mt: 0.15 }} />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{chk.label}</Typography>
                  {chk.detail && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{chk.detail}</Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>

      {/* Sticky bottom action bar */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          mt: 2,
          py: 1.5,
          px: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: c.panel,
        }}
      >
        <Button onClick={() => navigate('/sales/invoices')} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
          {submitting
            ? 'Creating…'
            : useOrderBasedCreate
            ? `Bill Entire Order ${soRef?.orderNumber}`
            : useDeliveryBasedCreate
            ? `Bill Delivery ${deliveryRef?.delivery_number}`
            : 'Save Invoice'}
        </Button>
      </Paper>
    </Box>
  );
};

export default InvoiceFormPage;
